import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Package, 
  Calendar, 
  DollarSign, 
  FileText,
  User,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Download
} from 'lucide-react';
import { purchasesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import dayjs from '../../utils/dayjs';
import TotalsBar from '../ui/TotalsBar';
import { usePurchasePdf } from '../../hooks/usePurchasePdf';

const PurchaseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();
  const { downloadForPurchase } = usePurchasePdf();

  // State
  const [purchase, setPurchase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch purchase data
  const fetchPurchase = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await purchasesAPI.get(id);
      setPurchase(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load purchase on mount
  useEffect(() => {
    fetchPurchase();
  }, [id]);

  // Take this draft purchase live (applies its quantities to stock).
  const handleGoLive = async () => {
    if (!window.confirm('Take this purchase live? This will add its quantities to stock.')) return;
    try {
      setIsPublishing(true);
      await purchasesAPI.publish(id);
      addNotification({
        type: 'success',
        title: 'Success',
        message: 'Purchase is now live'
      });
      // The draft skipped its PDF; it earns one now that stock has moved.
      await downloadForPurchase(id);
      fetchPurchase();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to take purchase live'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Re-download the purchase order for a live purchase. Covers the ones that
  // went live before this feature existed, and any download that was lost or
  // blocked at the moment the purchase was created.
  const handleDownloadPdf = async () => {
    try {
      setIsDownloading(true);
      await downloadForPurchase(id);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this draft purchase?')) {
      try {
        setIsDeleting(true);
        await purchasesAPI.remove(id);
        addNotification({
          type: 'success',
          title: 'Success',
          message: 'Purchase deleted successfully'
        });
        navigate('/purchases');
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: error.response?.data?.detail || 'Failed to delete purchase'
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Purchase</h3>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Purchases
          </button>
        </div>
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Purchase Not Found</h3>
          <p className="text-gray-600 mb-4">The purchase you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Purchases
          </button>
        </div>
      </div>
    );
  }

  const isDraft = String(purchase.Status || '').toLowerCase() === 'draft';

  return (
    <div className="space-y-6">
      {isDraft && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          This purchase is a draft — its quantities have not been added to stock yet. Click “Go Live” to apply them.
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/purchases')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                Purchase #{purchase.ID}
              </h1>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  isDraft ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                }`}
              >
                {isDraft ? 'Draft' : 'Live'}
              </span>
            </div>
            <p className="text-gray-600">Purchase order details</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Live only: a draft has not gone live, so it has no purchase order
              to hand the stockroom yet. */}
          {!isDraft && (
            <button
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Download this purchase order as a PDF"
            >
              <Download className="h-4 w-4" />
              {isDownloading ? 'Preparing...' : 'Download PDF'}
            </button>
          )}
          {isDraft && (
            <button
              onClick={handleGoLive}
              disabled={isPublishing}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" />
              {isPublishing ? 'Going live...' : 'Go Live'}
            </button>
          )}
          {isDraft && (
            <button
              onClick={() => navigate(`/purchases/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
          )}
          {isDraft && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <TotalsBar details={purchase.details} overallDiscount={purchase.Discount} />

      {/* Purchase Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Purchase Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Purchase Date</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {purchase.PurchaseDate ? dayjs(purchase.PurchaseDate).format('MMM DD, YYYY') : '-'}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{'Counter Sale'}</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {purchase.CreatedDate ? dayjs(purchase.CreatedDate).format('MMM DD, YYYY h:mm A') : '-'}
                </span>
              </div>
            </div>
            
    
          </div>
        </div>

        {(purchase.PurchaseRemarks || purchase.Remarks) && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-medium text-gray-700">Remarks</h3>
            </div>
            <div className="space-y-2">
              {purchase.PurchaseRemarks && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Purchase Remarks</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{purchase.PurchaseRemarks}</p>
                </div>
              )}
              {purchase.Remarks && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Additional Remarks</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{purchase.Remarks}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Purchase Items */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Purchase Items</h2>
        </div>

        {purchase.details && purchase.details.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {purchase.details.map((detail, index) => {
                  const itemTotal = (parseFloat(detail.Price) - parseFloat(detail.Discount || 0)) * parseFloat(detail.Quantity);
                  return (
                    <tr key={detail.ID || index} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {detail.item?.Name}`
                          </div>
                          {detail.item?.Barcode && (
                            <div className="text-xs text-gray-500">Barcode: {detail.item.Barcode}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {detail.Quantity}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatPrice(detail.Price, 'PKR', false)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatPrice(detail.Discount || 0, 'PKR', false)}
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {formatPrice(itemTotal, 'PKR', false)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {detail.Remarks || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No items in this purchase</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default PurchaseDetail;
